import * as React from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useModal } from 'framework/components/ui/Modal';
import { prefixClassName } from 'framework/components/ui/_conf';
import { i18n } from 'i18n';
import axe, { AxeResults } from 'axe-core';
import { RichEditorFieldProps } from './Types';
import { useFeatureIsOn } from '../../../../growthbook/useFeatureIsOn';
import { AccessibilityResultsModal } from './AccessibilityModal/AccessibilityResultsModal';
import {
  defaultPlugins,
  defaultToolbarButtons,
  defaultCustomMenus,
  COLOR_MENU,
  FORE_COLOR_MENU_ITEM,
  BACK_COLOR_MENU_ITEM,
} from './Defaults';
import './RichEditor.scss';

const cssClass = prefixClassName('rich-editor');
const editorInvalidElements =
  'table,img,h1,h2,h3,h4,h5,h6,br,div,style,html,head,body,canvas,audio,video,iframe,object,form';
const scriptElement = 'script[language|type]';

const singleRootParagraph = /^<p(\s[^>]*)?>([\s\S]*)<\/p>$/i;

/**
 * Splits a lone root paragraph into its attributes and inner content, or returns undefined when
 * the content is anything else. Sibling paragraphs have to be rejected explicitly: the regex is
 * greedy, so `<p>a</p><p>b</p>` matches as one paragraph whose inner content is `a</p><p>b`.
 */
const matchSingleRootParagraph = (html: string): [string, string] | undefined => {
  const trimmed = typeof html === 'string' ? html.trim() : '';
  const match = trimmed ? singleRootParagraph.exec(trimmed) : null;
  if (!match) return undefined;

  const [, attributes, inner] = match;
  if (/<\/p>/i.test(inner) || /<p[\s>]/i.test(inner)) return undefined;

  return [attributes, inner];
};

export const hasRootParagraph = (html: string): boolean => !!matchSingleRootParagraph(html);

/**
 * TinyMCE 7 removed the ability to turn `forced_root_block` off: the option validator accepts
 * only a non-empty string, so both `false` and `''` are discarded in favour of the 'p' default.
 * Bare content therefore serializes wrapped — `QuickMenu` becomes `<p>QuickMenu</p>` — which
 * breaks mobile clients that match Freeform values as exact raw component tokens. Since the
 * wrapper cannot be prevented, only the *forced* one is undone on serialization, restoring the
 * pre-upgrade shape where bare content stayed bare.
 *
 * `rootBlockWasAuthored` says whether the content last set into the editor already carried a
 * root paragraph. When it did, the paragraph is the author's own and is kept — writing
 * `<p>Test</p>` in the source view has to survive a round trip. Sibling paragraphs and a
 * wrapper carrying attributes are kept for the same reason: paragraph-level formatting such as
 * `text-align` lives on the `<p>` and would be silently lost with it.
 */
export const unwrapForcedRootBlock = (
  html: string,
  rootBlockWasAuthored = false,
  rawValues: string[] = [],
): string => {
  const match = matchSingleRootParagraph(html);
  if (!match) return html;

  const [attributes, inner] = match;
  const content = inner.trim();
  if (!content) return html;

  /*
    A value the consumer requires verbatim is unwrapped unconditionally. Nothing in the editor
    can tell an authored paragraph apart from one whose text was replaced — TinyMCE reuses the
    same element for select-all-and-type, so neither the DOM nor `forced_root_block_attrs`
    marks it — which leaves the value itself as the only dependable signal.
  */
  if (rawValues.includes(content)) return content;

  if (attributes || rootBlockWasAuthored) return html;

  return content;
};

export const RichEditor = (props: RichEditorFieldProps) => {
  const {
    value,
    onChange,
    excludeOnlyPlugins,
    includeOnlyPlugins,
    includeOnlyButtons,
    excludeOnlyButtons,
    onOpenDam,
    disabled,
    advancedSettings,
    restrictInvalidElements,
    disableImagePasting,
    valid_children,
    disableRootBlock,
    rawValues,
    customSetup,
    allowScript,
    onDisallowedScriptContent,
    allowColorPicker,
    allowAccessibilityResults,
    allowUnsandboxedIframes,
    protectPatterns,
    name,
  } = props;

  const showAccessibilityButton = useFeatureIsOn('dev-tinymce-a11y-scan') && allowAccessibilityResults;
  const skipNormalizationRef = React.useRef(false);
  const strippedScriptContentRef = React.useRef(false);
  // Whether the content last set into the editor already carried a root paragraph, i.e. the
  // paragraph is the author's own rather than one TinyMCE forced on to bare content.
  const rootBlockWasAuthoredRef = React.useRef(false);

  const EDITOR_ID = `editorContent-${name}`;
  const ACCESSIBILITY_RESULTS_MODAL = `accessibilityResultsModal-${name}`;

  const { openModal } = useModal(ACCESSIBILITY_RESULTS_MODAL);

  const [accessibilityResults, setAccessibilityResults] = React.useState<AxeResults>(null);

  const buildPlugins = () => {
    let plugins = includeOnlyPlugins || defaultPlugins;
    if (excludeOnlyPlugins) {
      plugins = plugins.filter((p) => !excludeOnlyPlugins.find((s) => s === p));
    }
    return plugins.join(' ');
  };

  const buildToolbarButtons = () => {
    let toolbar = '';
    Object.keys(defaultToolbarButtons).forEach((key) => {
      if (!allowColorPicker && key === COLOR_MENU) return;

      const menus = defaultToolbarButtons[key].filter((m) => {
        /*
          select buttom:
            1. if "includeOnlyButtons" provided then should be included in "includeOnlyButtons" list
            2. if "excludeOnlyButtons" provided then should be excluded if it is found in "excludeOnlyButtons" list
        */
        const include =
          (!includeOnlyButtons || !!includeOnlyButtons.find((b) => b === m)) &&
          !excludeOnlyButtons?.find((b) => b === m);
        return include;
      });
      toolbar = `${toolbar} ${menus.join(' ')} |`;
    });
    return `${toolbar}`;
  };

  const customiseMenuBar = () => {
    const { includeOnlyMenuItems, excludeOnlyMenuItems } = props;

    const menuBar = Object.keys(defaultCustomMenus).reduce((acc, key) => {
      const { items } = defaultCustomMenus[key];

      const stringified = items.reduce((ac, list) => {
        const menus = list.filter((m) => {
          const includeMenu = !includeOnlyMenuItems || includeOnlyMenuItems.some((s) => s === m);
          const excludeMenu = !!excludeOnlyMenuItems && excludeOnlyMenuItems.some((s) => s === m);
          if (!allowColorPicker) {
            return includeMenu && !excludeMenu && m !== FORE_COLOR_MENU_ITEM && m !== BACK_COLOR_MENU_ITEM;
          }
          return includeMenu && !excludeMenu;
        });

        return `${ac} ${menus.join(' ')} |`;
      }, '');

      return {
        ...acc,
        [key]: {
          title: defaultCustomMenus[key].title(),
          items: stringified,
        },
      };
    }, {});
    return menuBar;
  };

  const handleAccessibilityClick = () => {
    openModal();
    axe.configure({
      reporter: 'v2',
    });
    axe.run([`#${EDITOR_ID}_ifr`],
      {
        runOnly: {
          type: 'tag',
          values: ['wcag2a', 'wcag2aa', 'wcag2aaa', 'wcag21a', 'wcag21aa', 'wcag22aa', 'EN-301-549']
        },
      },
      (_, results) => {
        setAccessibilityResults(results);
      }
    );
  };

  const handleChange = (val: string) => {
    // Emptying the editor destroys whatever paragraph the author had, so a wrapper appearing
    // after this point was forced on to bare content and has to be unwrapped again. Without
    // this, replacing prose with a component token in one sitting would keep it wrapped.
    if (disableRootBlock && !val?.trim()) {
      rootBlockWasAuthoredRef.current = false;
    }
    if (skipNormalizationRef.current) {
      skipNormalizationRef.current = false;
      return;
    }
    if (onChange) {
      onChange(val);
    }
  };

  const handleAction = (editor: any): void => {
    if (onOpenDam) {
      onOpenDam(editor);
    }
  };

  const { hideMenuBar, pasteAsText, height } = props;
  const hideMenu = hideMenuBar ? { menubar: false } : {};
  const pasteAsTextOption = pasteAsText ? { paste_as_text: true } : {};

  // Use protect to restrict solve issues https://handlebarsjs.com/builtin_helpers.html
  const handlebarsProtect = advancedSettings
    ? [
      /{{#if.+?}}/g, // protect {{if*}}
      /{{\/if}}/g, // protect closing {{if}}
      /{{else}}/g, // protect {{else}}
      /{{#each.+?}}/g, // protect {{each*}}
      /{{\/each}}/g, // protect closing {{each}}
      /{{#unless.+?}}/g, // protect {{unless*}}
      /{{\/unless}}/g, // protect closing {{unless}}
      /{{#with.+?}}/g, // protect {{with*}}
      /{{\/with}}/g, // protect closing {{with}}
      /{{this}}/g, // protect {{this}},
      /{{#ifcond.+?}}/g, // protect {{ifcond*}}
      /{{\/ifcond}}/g, // protect closing {{ifcond}}
    ]
    : [];
  /*
    A caller's patterns run first. TinyMCE swaps each match for an escaped comment and restores
    them in one pass, so a match nested inside a later one would come back as a raw
    `<!--mce:protected` comment; a Handlebars comment wrapping `{{#if}}` has to be taken whole.
  */
  const protect = [...(protectPatterns ?? []), ...handlebarsProtect];
  const protectOptions = protect.length ? { protect } : {};

  const invalidElements = restrictInvalidElements ? { invalid_elements: editorInvalidElements } : {};
  const extendedValidElements = allowScript ? scriptElement : undefined;

  /*
    `@tinymce/tinymce-react` reads `init` only while mounting, so everything captured in
    `setup` and in the schema options keeps its mount-time value for the life of the
    instance. Both options below are driven by GrowthBook flags, which resolve
    asynchronously and read as off until the payload arrives, so keying the editor on them
    re-initialises TinyMCE when a flag settles after mount. `value` is controlled, so the
    content survives the remount; only the undo history and cursor position are lost.
  */
  const editorInstanceKey = [
    allowScript ? 'scripts' : 'noScripts',
    showAccessibilityButton ? 'a11y' : 'noA11y',
  ].join('-');

  const handlePaste = (e) => {
    const { items } = e.clipboardData;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        return;
      }
    }
  };

  const handleDeleteDisallowedScriptContent = (e) => {
    const original = e.content ?? '';
    const cleaned = original
      .replace(/\{\{scriptStart\}\}[\s\S]*?\{\{scriptEnd\}\}/gi, '')
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '');

    e.content = cleaned;

    if (cleaned !== original) {
      strippedScriptContentRef.current = true;
      onDisallowedScriptContent?.();
    }
  };

  // Only a full replacement (`set`) reveals the author's own markup; an insertion at the cursor
  // arrives as `selection` with just the inserted fragment.
  const handleTrackAuthoredRootBlock = (e) => {
    if (!e.set || e.selection) return;
    rootBlockWasAuthoredRef.current = hasRootParagraph(e.content);
  };

  // Runs on serialization rather than in onChange so `editor.getContent()` and the controlled
  // `value` stay identical — otherwise tinymce-react's rollback/setContent reconciliation would
  // keep re-applying the wrapped content and fight the edit. The `format` guard leaves the
  // 'raw' serializations the undo manager relies on untouched.
  const handleUnwrapForcedRootBlock = (e) => {
    if (e.selection || e.format !== 'html') return;
    e.content = unwrapForcedRootBlock(e.content, rootBlockWasAuthoredRef.current, rawValues);
  };

  return (
    <div className={cssClass}>
      <Editor
        key={editorInstanceKey}
        id={EDITOR_ID}
        tinymceScriptSrc={`${process.env.PUBLIC_URL}/jscripts/tinymce/tinymce.min.js`}
        init={{
          promotion: false,
          verify_html: false,
          resize: false,
          entity_encoding: 'named+numeric',
          remove_trailing_brs: false,
          fix_list_elements: false,
          // `forced_root_block` is deliberately not set here: TinyMCE 7 rejects every value that
          // would disable it (`false` and `''` both fail the non-empty-string validator and log a
          // warning) so it can only be undone on serialization — see unwrapForcedRootBlock.

          // TinyMCE 7 defaults `sandbox_iframes` to true, which injects `sandbox=""`
          // onto any iframe outside the built-in exclusions list and disables scripts
          // inside it. Opt out where verbatim third-party embeds are required.
          sandbox_iframes: !allowUnsandboxedIframes,
          plugins: buildPlugins(),
          toolbar: buildToolbarButtons(),
          document_base_url: `${window.location.protocol}//${window.location.host}/`,
          convert_urls: false,
          height,
          ...hideMenu,
          menu: customiseMenuBar(),
          quickbars_insert_toolbar: false,
          ...pasteAsTextOption,
          file_picker_types: 'image',
          ...protectOptions,
          ...invalidElements,
          valid_children,
          extended_valid_elements: extendedValidElements,
          setup: (editor) => {
            if (disableImagePasting) {
              editor.on('paste', handlePaste);
            }
            if (!allowScript) {
              editor.on('BeforeSetContent', handleDeleteDisallowedScriptContent);
            }
            if (disableRootBlock) {
              editor.on('BeforeSetContent', handleTrackAuthoredRootBlock);
              editor.on('GetContent', handleUnwrapForcedRootBlock);
            }
            editor.ui.registry.addButton('dam', {
              icon: 'image',
              onAction: () => handleAction(editor),
            });
            editor.ui.registry.addMenuItem('dam', {
              icon: 'image',
              text: `${i18n.t('components.richeditor.image')}`,
              onAction: () => handleAction(editor),
            });

            if (showAccessibilityButton) {
              editor.ui.registry.addButton('accessibility', {
                icon: 'accessibility-check',
                tooltip: `${i18n.t('components.richeditor.accessibility')}`,
                onAction: () => handleAccessibilityClick(),
              });
              editor.on('init', () => {
                const head = editor.dom.select('head')[0];

                editor.dom.add(
                  head,
                  'script',
                  {
                    src: `/jscripts/axecore/axe.min.js`,
                    type: 'text/javascript'
                  }
                );
              })
            }

            if (customSetup) {
              customSetup(editor);
            }
          },
        }}
        value={value}
        disabled={disabled}
        onInit={(evt, editor) => {
          // Seeded from the prop as well as from BeforeSetContent, because tinymce-react loads
          // the initial value through a `no_events: true` setContent on some paths. Must run
          // before getContent(), which is what applies the unwrap.
          rootBlockWasAuthoredRef.current = hasRootParagraph(value);
          const normalized = editor.getContent();
          const strippedScriptContent = strippedScriptContentRef.current;
          strippedScriptContentRef.current = false;

          if (normalized === value) {
            return;
          }
          if (strippedScriptContent) {
            onChange?.(normalized);
            return;
          }
          /*
            A wrapper dropped on load is a deliberate rewrite, not normalization to suppress, so
            the form value has to follow it. Leaving it behind would keep the stale wrapped value
            on a save without edits, and would strand `value` out of step with getContent() —
            which tinymce-react keeps trying to reconcile by re-setting the content.
          */
          if (disableRootBlock
            && normalized === unwrapForcedRootBlock(value, rootBlockWasAuthoredRef.current, rawValues)) {
            onChange?.(normalized);
            return;
          }
          skipNormalizationRef.current = true;
        }}
        onEditorChange={handleChange}
      />
      {showAccessibilityButton &&
        <AccessibilityResultsModal
          modalName={ACCESSIBILITY_RESULTS_MODAL}
          results={accessibilityResults}
          setAccessibilityResults={setAccessibilityResults}
        />
      }
    </div>
  );
};
